import { useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FileX2, SearchX } from "lucide-react";
import { FeedbackState, Segmented, type DataTableColumn } from "@h2j-data-team/ui";
import type { CapacityRowsSort, CapacityUsageRow } from "@repo/shared";

import {
  ListError,
  ListLoading,
  QvdListToolbar,
  QvdPathLabel,
  SpaceLink,
} from "@/components/qvd-analysis/qvd-list-parts";
import { SERVER_SORT_DIRECTION, useDebouncedValue } from "@/components/qvd-analysis/qvd-list-utils";
import { VirtualRowList } from "@/components/shared/VirtualRowList";
import { formatBytes } from "@/lib/health-format";
import {
  capacitySpacesQueryOptions,
  capacityUsageRowsInfiniteQueryOptions,
} from "@/features/capacity/api";
import { CapacityBySpaceView } from "./CapacityBySpaceView";

type FileView = "flat" | "bySpace";

/**
 * Per-datafile consumption of the measured day (`Datafile` rows carry no app),
 * either as the flat ranking — searched, space-filtered and sorted server-side,
 * virtualized and paged on scroll — or aggregated by space and folder tree.
 */
export function CapacityByFileTable({ snapshotId }: { snapshotId: string }) {
  const { t } = useTranslation("capacity");
  const [view, setView] = useState<FileView>("flat");
  const [search, setSearch] = useState("");
  const [spaceFilter, setSpaceFilter] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<CapacityRowsSort>("size");
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data: spaces } = useQuery(capacitySpacesQueryOptions(snapshotId, "datafile"));

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    capacityUsageRowsInfiniteQueryOptions(snapshotId, "datafile", {
      search: debouncedSearch,
      space: spaceFilter[0] ?? "",
      sort: sortKey,
    }),
  );

  const rows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const filtering = !!debouncedSearch || spaceFilter.length > 0;

  const columns = useMemo<DataTableColumn<CapacityUsageRow>[]>(
    () => [
      {
        id: "name",
        header: t("byFile.cols.file"),
        sort: "text",
        cell: (row) =>
          row.datafileName ? (
            <QvdPathLabel path={row.datafileName} />
          ) : (
            // Name never resolved: the id IS the identification.
            <span className="block truncate font-mono">{row.datafileId ?? "—"}</span>
          ),
      },
      {
        id: "space",
        header: t("byFile.cols.space"),
        width: 220,
        cell: (row) => (
          <span className="text-dim">
            <SpaceLink spaceName={row.spaceName} />
          </span>
        ),
      },
      {
        id: "size",
        header: t("byFile.cols.bytes"),
        width: 120,
        align: "end",
        variant: "numeric",
        sort: "number",
        cell: (row) => formatBytes(row.sizeBytes),
      },
    ],
    [t],
  );

  const viewSwitch = (
    <div className="ml-auto">
      <Segmented<FileView>
        size="compact"
        label={t("view.label")}
        value={view}
        onValueChange={setView}
        options={[
          { value: "flat", label: t("view.flat") },
          { value: "bySpace", label: t("view.bySpace") },
        ]}
      />
    </div>
  );

  return (
    <div className="flex flex-col">
      {/* Outside the loading branch: a filter change starts a fresh query, and
          unmounting the toolbar under it would drop the focused search box. */}
      {view === "flat" ? (
        <QvdListToolbar
          search={search}
          onSearch={setSearch}
          searchPlaceholder={t("byFile.searchPlaceholder")}
          spaceOptions={(spaces?.data ?? []).map((s) => s.spaceName)}
          spaceFilter={spaceFilter}
          onSpaceFilter={setSpaceFilter}
          singleSpace
        >
          {viewSwitch}
        </QvdListToolbar>
      ) : (
        <div className="flex pb-3">{viewSwitch}</div>
      )}

      {view === "bySpace" ? (
        <CapacityBySpaceView snapshotId={snapshotId} />
      ) : isLoading ? (
        <ListLoading />
      ) : isError ? (
        <ListError error={error} title={t("error.load")} onRetry={() => void refetch()} />
      ) : rows.length === 0 ? (
        <FeedbackState
          state={filtering ? "no-results" : "empty"}
          icon={filtering ? <SearchX /> : <FileX2 />}
          title={filtering ? t("byFile.emptyFiltered") : t("byFile.empty")}
        />
      ) : (
        <VirtualRowList
          label={t("tabs.byFile")}
          columns={columns}
          rows={rows}
          getKey={(row) => row.id}
          sort={{ id: sortKey, direction: SERVER_SORT_DIRECTION[sortKey] }}
          onSort={(column) =>
            setSortKey(column.id === sortKey ? "size" : (column.id as CapacityRowsSort))
          }
          onReachEnd={() => {
            if (hasNextPage && !isFetchingNextPage) fetchNextPage();
          }}
          loadingMoreLabel={isFetchingNextPage ? t("loadingMore") : undefined}
        />
      )}
    </div>
  );
}
